import { creatorbase, creatorAgencyQueries } from '@round/api';
import { ButtonShell, Image, SkeletonizedValue } from '@round/ui-kit';
import styles from './PaymentRequest.module.css';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import { asMoneyWithoutZeros, showNotification } from 'helpers';
import moment from 'moment';
import cn from 'classnames';
import { TooltipOnOverflow, assets } from '@round/whitelabel-ui';
import copy from 'copy-to-clipboard';
import { useState } from 'react';
import { file, network, toDecimalPoint } from '@round/utils';
import { Button } from '@round/whitelabel-ui';
import { Tooltip } from '@round/whitelabel-ui';
import { getPrimaryPaymentStatus } from 'Modules/Plans/Finance/helpers';
import PaymentStatusBadge from 'Modules/Plans/Finance/components/PaymentStatusBadge/PaymentStatusBadge';
import { useClientPayees } from '@round/creatorbase-helpers/api-hooks';
import { useQuery } from '@tanstack/react-query';

const {
    Copy: CopyIcon,
    Download: DownloadIcon,
    File: FileIcon,
    PayPalShort: PayPalIcon,
    TipaltiShort: TipaltiIcon,
    UserGroup: UserGroupIcon,
} = assets;

type Props = {
    data: creatorbase.PostPaymentRequest;
};

const handleCopy = (value: string) => {
    const success = copy(value);
    if (success) {
        showNotification('Copied!', 'info');
    }
};

const getFileName = (url?: string | null) => {
    if (!url) {
        return 'No file';
    }

    return url.split('/').reverse()[0].split('?')[0];
};

const PaymentRequest = ({ data }: Props) => {
    const shouldDisplayVAT = data.provider_name !== 'OTHER' && !!data.vat_amount;
    const { getCurrencyById } = useCurrencies();

    const payeeId = data?.tipalti_payee_id;

    const { data: payee, isFetching: isPayeeFetching } = useClientPayees(
        { tipalti_payee_id: payeeId ?? undefined, page_size: 1 },
        {
            select: (data) => data.results[0] || null,
            enabled: !!payeeId && data.provider_name === 'TIPALTI',
        }
    );

    const agencyPayeeId = data.creator_agency_payee_id;

    const { data: agencyPayee, isLoading: isAgencyPayeeLoading } = useQuery({
        ...creatorAgencyQueries.payee.options.list({ id: agencyPayeeId ?? undefined, page_size: 1 }),
        select: (data) => data.results[0] || null,
        enabled: !!agencyPayeeId && data.provider_name === 'CREATOR_AGENCY',
    });

    const [isFileDownloading, setIsFileDownloading] = useState(false);
    const handleDownload = async () => {
        if (!data.file_upload) {
            return;
        }

        try {
            setIsFileDownloading(true);
            const response = await network.fetchFile(data.file_upload);
            if (response instanceof File) {
                file.downloadFile(response);
                return;
            }

            showNotification('Could not download file', 'error');
        } catch (e) {
            showNotification('Could not download file', 'error');
        } finally {
            setIsFileDownloading(false);
        }
    };

    const paymentStatus = getPrimaryPaymentStatus(data);

    return (
        <div className={styles.requestContainer}>
            <div className={styles.referenceContainer}>
                <div className={styles.copyRow}>
                    <TooltipOnOverflow tooltip={data.payment_reference}>
                        <p className={styles.reference}>{data.payment_reference}</p>
                    </TooltipOnOverflow>
                    <ButtonShell className={styles.copyButton} onClick={() => handleCopy(data.payment_reference)}>
                        <CopyIcon />
                    </ButtonShell>
                </div>
                <div className={styles.detailsRow}>
                    <p className={styles.amount}>
                        {asMoneyWithoutZeros(Number(data.amount), getCurrencyById(data.currency_id))}
                        {shouldDisplayVAT && (
                            <Tooltip.Root>
                                {(anchor, setAnchor) => (
                                    <>
                                        <span ref={setAnchor} className={styles.amountVAT}>
                                            (+{asMoneyWithoutZeros(data.vat_amount, getCurrencyById(data.currency_id))}{' '}
                                            VAT)
                                        </span>
                                        <Tooltip anchorElement={anchor}>
                                            <Tooltip.Body>
                                                <span>{toDecimalPoint(data.vat_rate * 100, 2)}% VAT</span>
                                            </Tooltip.Body>
                                        </Tooltip>
                                    </>
                                )}
                            </Tooltip.Root>
                        )}
                    </p>
                    <p className={styles.date}>{moment(data.created_at).format('DD MMM YYYY, HH:mm')}</p>
                </div>
            </div>

            <div className={styles.paymentDetailsContainer}>
                <SkeletonizedValue
                    isInitialized={!isPayeeFetching && !isAgencyPayeeLoading}
                    width="100%"
                    height="0.875rem"
                >
                    {data.provider_name === 'PAYPAL' ? (
                        <div className={styles.copyRow}>
                            <PayPalIcon />
                            <p className={styles.paymentDetails}>{data.paypal_email_address || '-'}</p>
                        </div>
                    ) : data.provider_name === 'TIPALTI' ? (
                        <div className={styles.copyRow}>
                            <TipaltiIcon />
                            <p className={styles.paymentDetails}>{payee?.email_address || '-'}</p>
                        </div>
                    ) : data.provider_name === 'CREATOR_AGENCY' ? (
                        <div className={styles.copyRow}>
                            {agencyPayee?.image ? (
                                <Image
                                    src={agencyPayee.image}
                                    alt={`${agencyPayee.name} image`}
                                    className={styles.agencyImage}
                                />
                            ) : (
                                <UserGroupIcon className={styles.agencyIcon} />
                            )}
                            <p className={styles.paymentDetails}>{data.creator_agency_payee_name || '-'}</p>
                        </div>
                    ) : null}
                </SkeletonizedValue>
                {data.provider_name === 'CREATOR_AGENCY' && data.creator_agency_payment_batch_id ? (
                    <div className={styles.copyRow}>
                        <p className={styles.paymentDetails}>Batch: {data.creator_agency_payment_batch_id}</p>
                        <ButtonShell
                            className={styles.copyButton}
                            onClick={() => handleCopy(data.creator_agency_payment_batch_id!)}
                        >
                            <CopyIcon />
                        </ButtonShell>
                    </div>
                ) : null}
                {data.notes ? (
                    <div className={styles.copyRow}>
                        <TooltipOnOverflow tooltip={data.notes}>
                            <p className={styles.paymentDetails}>{data.notes}</p>
                        </TooltipOnOverflow>
                    </div>
                ) : null}
            </div>

            {!!data.file_upload ? (
                <Tooltip.Root>
                    {(anchor, setAnchor) => (
                        <>
                            <Button
                                ref={setAnchor}
                                className={cn(styles.fileButton, { [styles.loading]: isFileDownloading })}
                                appearance="ghost"
                                type="button"
                                isLoading={isFileDownloading}
                                onClick={handleDownload}
                            >
                                <FileIcon className={styles.fileIcon} />
                                <DownloadIcon className={styles.downloadIcon} />
                            </Button>

                            <Tooltip anchorElement={anchor}>
                                <Tooltip.Body>{getFileName(data.file_upload)}</Tooltip.Body>
                            </Tooltip>
                        </>
                    )}
                </Tooltip.Root>
            ) : null}

            <div className={styles.section}>
                <PaymentStatusBadge status={paymentStatus} />
            </div>
        </div>
    );
};

export default PaymentRequest;
